import { Link } from '@tanstack/react-router';
import { ArrowUpRight, Minus, Plus } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@worldcoo/ui/components/accordion';
import { useNgoAreaUrl } from '@/lib/ngo-area';

/**
 * One question and its answer.
 *
 * `bullets` is the hand-off's bulleted list; `link` is for an answer that ends by sending the reader
 * somewhere — the FAQ page's content asks for it twice, at "what requirements" (the selection criteria)
 * and "how to start collaborating" (the sign-up form). Both stay optional so the NGO page's four
 * questions, which have neither, are unaffected.
 */
export type FaqItem = {
	question: string;
	answer: string;
	bullets?: string[];
	/** A closing sentence AFTER the list, which some answers need — "the solution adapts to each company". */
	outro?: string;
	link?: { label: string; target: FaqLinkTarget };
};

/**
 * Where an answer may send the reader.
 *
 * A closed set rather than a path, because the items come from a JSON content file: a raw string would
 * either need casting past `Link`'s typed route union or would let a translator invent a route that
 * does not exist. Naming the destination keeps the check at compile time and lets the locale files
 * carry only the label.
 */
export type FaqLinkTarget = 'selection-criteria' | 'ngo-signup' | 'contact' | 'contact-donor' | 'privacy-policy';

/**
 * The tiny filled heart the hand-off uses as a list bullet (8px, `vuesax/linear/heart`). Inlined
 * rather than shipped as a file — it is one path, and inlining lets it take the list's own colour.
 */
function HeartBullet() {
	return (
		<svg viewBox="0 0 8 8" className="mt-[0.45em] size-2 shrink-0" fill="currentColor" aria-hidden>
			<path d="M4 7.2C3.8 7.2 3.6 7.13 3.45 7L1.2 5.2C0.45 4.6 0 3.7 0 2.75 0 1.23 1.1 0 2.45 0c.6 0 1.15.24 1.55.63C4.4.24 4.95 0 5.55 0 6.9 0 8 1.23 8 2.75c0 .95-.45 1.85-1.2 2.45L4.55 7c-.15.13-.35.2-.55.2Z" />
		</svg>
	);
}

/** The answer's trailing link. Internal ones go through the router so the slug is rewritten per locale. */
function AnswerLink({ link }: { link: NonNullable<FaqItem['link']> }) {
	// Per-environment, like every other sign-up CTA on the site.
	const ngoArea = useNgoAreaUrl();
	const className = 'text-brand-strong mt-4 inline-flex items-center gap-1 font-medium hover:underline';
	const label = (
		<>
			{link.label}
			<ArrowUpRight className="size-4" />
		</>
	);

	if (link.target === 'ngo-signup') {
		return (
			<a href={ngoArea('/onboarding')} target="_blank" rel="noopener noreferrer" className={className}>
				{label}
			</a>
		);
	}
	// The donor answer goes to the donor questionnaire, not to the chooser: it is a refund question, and
	// the audience decides the category the enquiry is filed under.
	if (link.target === 'contact-donor') {
		return (
			<Link to="/www/contact/$audience" params={{ audience: 'donor' }} className={className}>
				{label}
			</Link>
		);
	}
	// A lookup rather than a chain of ternaries: the set is closed, so the compiler checks the map is
	// exhaustive and adding a destination is one entry instead of one more nested condition. The two
	// targets above are excluded rather than listed — one leaves the site and one carries route params,
	// so neither is a bare path this map could hold.
	const ROUTES = {
		'contact': '/www/contact',
		'selection-criteria': '/www/selection-criteria',
		'privacy-policy': '/www/privacy-policy',
	} as const satisfies Record<Exclude<FaqLinkTarget, 'ngo-signup' | 'contact-donor'>, string>;

	return (
		<Link to={ROUTES[link.target]} className={className}>
			{label}
		</Link>
	);
}

/**
 * The hand-off's accordion, shared by the NGO page's closing FAQ and the FAQ page itself.
 *
 * Extracted rather than duplicated because the marker is the fiddly part: the hand-off wants a solid
 * orange 32px tile whose plus becomes a MINUS when the item opens — two different glyphs, not one
 * rotated — so the Accordion's own pair of chevrons is HIDDEN rather than restyled. They carry
 * `data-slot="accordion-trigger-icon"`, which is also what the component's `ml-auto` rule targets,
 * hence the explicit `flex-1` on the question; without it the tile sits against the text instead of
 * the far edge. Copying that into a second page is how the two drift.
 */
export function FaqAccordion({ items }: { items: FaqItem[] }) {
	return (
		<Accordion type="single" collapsible className="flex w-full max-w-[842px] flex-col gap-2">
			{items.map((item) => (
				<AccordionItem
					key={item.question}
					value={item.question}
					className="hover:bg-sand data-[state=open]:bg-sand rounded-2xl border-none px-6 transition-colors lg:px-8"
				>
					<AccordionTrigger className="items-center gap-6 py-6 text-left text-lg leading-[1.3] font-medium tracking-[-0.02em] text-black hover:no-underline lg:py-8 lg:text-xl [&_[data-slot=accordion-trigger-icon]]:hidden">
						<span className="flex-1">{item.question}</span>
						<span className="bg-brand grid size-8 shrink-0 place-items-center rounded-lg text-white">
							<Plus className="size-4 group-aria-expanded/accordion-trigger:hidden" />
							<Minus className="hidden size-4 group-aria-expanded/accordion-trigger:block" />
						</span>
					</AccordionTrigger>
					<AccordionContent className="pb-6 lg:pb-8">
						<p className="text-ink/80 text-base leading-[1.5]">{item.answer}</p>
						{item.bullets && (
							<ul className="mt-4 flex flex-col gap-2">
								{item.bullets.map((bullet) => (
									<li key={bullet} className="text-ink flex items-start gap-2 text-base leading-[1.5]">
										<HeartBullet />
										{bullet}
									</li>
								))}
							</ul>
						)}
						{item.outro && <p className="text-ink/80 mt-4 text-base leading-[1.5]">{item.outro}</p>}
						{item.link && <AnswerLink link={item.link} />}
					</AccordionContent>
				</AccordionItem>
			))}
		</Accordion>
	);
}
